/**
 * The bill screen for one dining session: what has been ordered, what is owed,
 * and every sensitive action a cashier can take on it.
 *
 * EVERY sensitive action goes through `useConfirm({ requireReason: true })` and
 * passes the typed justification as `x-action-reason`. That is not politeness —
 * the API returns 403 REASON_REQUIRED (and 400 VALIDATION_FAILED) without it
 * for `order.cancel`, `order.item.cancel`, `order.discount`, `order.gift_item`,
 * `table.transfer|merge|split` and `invoice.reprint`.
 */

import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  MoneyText,
  PermissionGate,
  Select,
  StatusBadge,
  useConfirm,
  useToast,
} from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { useAsync, errorMessage } from '../../lib/hooks';
import { hhmm, vnd } from '../../lib/format';
import { printHtml } from '../../lib/print';
import { renderQrSvg } from '../../lib/qr';
import { buildQrSlipHtmlFromPayload, buildReceiptHtml } from './receipt';
import { PaymentScreen } from './PaymentScreen';
import type { FloorMapTable, InvoiceResponse, SessionDetailResponse, TransferType } from '../../lib/types';
import { isExistingInvoice } from '../../lib/types';

export interface SessionScreenProps {
  readonly branchId: string;
  readonly sessionId: string;
  readonly table: FloorMapTable | null;
  readonly tables: readonly FloorMapTable[];
  readonly cashSessionId: string | null;
  readonly onBack: () => void;
  readonly onAddItems: (orderId: string | null) => void;
  readonly onChanged: () => void;
}

export function SessionScreen({
  branchId,
  sessionId,
  table,
  tables,
  cashSessionId,
  onBack,
  onAddItems,
  onChanged,
}: SessionScreenProps): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const detail = useAsync<SessionDetailResponse>((signal) => api.orders.session(sessionId, { signal }), [sessionId]);
  const [paying, setPaying] = useState(false);
  const [transfer, setTransfer] = useState<TransferType | null>(null);
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [printingQr, setPrintingQr] = useState(false);

  const tableLabel = table?.displayName ?? detail.data?.session.session_code ?? 'Mang về';
  const exponent = detail.data?.orders[0]?.currency_exponent ?? 0;
  const currency = detail.data?.orders[0]?.currency ?? 'VND';

  const refresh = (): void => {
    detail.reload();
    onChanged();
  };

  // --- sensitive actions ----------------------------------------------------

  const applyDiscount = async (): Promise<void> => {
    const order = detail.data?.orders.find((o) => o.status !== 'CANCELLED');
    if (order === undefined) return;
    const reason = await confirm({
      title: 'Giảm giá đơn hàng',
      message: 'Nhập tỉ lệ giảm giá (%) sẽ được áp cho toàn bộ đơn. Thao tác này được ghi nhật ký.',
      requireReason: true,
      reasonLabel: 'Lý do giảm giá',
      reasonSuggestions: ['Khách quen', 'Khiếu nại chất lượng', 'Chương trình khuyến mãi', 'Quản lý duyệt'],
      confirmLabel: 'Giảm 10%',
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      const result = await api.orders.discount(order.id, { discountType: 'PERCENT', valueBp: 1000 }, reason);
      toast.success(`Đã giảm ${vnd(result.amountMinor, currency, exponent)}`);
      refresh();
    } catch (cause: unknown) {
      // 403 with details.maxBp = over the branch ceiling without
      // `order.discount.override`. The API's Vietnamese message says so.
      toast.error('Không giảm giá được', errorMessage(cause));
    }
  };

  const giftItem = async (orderId: string, itemId: string, name: string): Promise<void> => {
    const reason = await confirm({
      title: `Tặng món "${name}"`,
      message: 'Món sẽ được tính 0đ và ghi nhận người duyệt. Không có thao tác hoàn tác.',
      requireReason: true,
      reasonLabel: 'Lý do tặng món',
      reasonSuggestions: ['Xin lỗi khách vì phục vụ chậm', 'Món lỗi', 'Khách VIP', 'Quản lý duyệt'],
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      await api.orders.giftItem(orderId, itemId, reason);
      toast.success('Đã tặng món');
      refresh();
    } catch (cause: unknown) {
      toast.error('Không tặng được món', errorMessage(cause));
    }
  };

  const cancelItem = async (orderId: string, itemId: string, name: string): Promise<void> => {
    const reason = await confirm({
      title: `Huỷ món "${name}"`,
      requireReason: true,
      reasonLabel: 'Lý do huỷ món',
      reasonSuggestions: ['Khách đổi ý', 'Hết nguyên liệu', 'Bấm nhầm', 'Món lỗi'],
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      await api.orders.cancelItem(orderId, itemId, reason);
      toast.success('Đã huỷ món');
      refresh();
    } catch (cause: unknown) {
      toast.error('Không huỷ được món', errorMessage(cause));
    }
  };

  const sendToKitchen = async (orderId: string): Promise<void> => {
    try {
      const result = await api.orders.confirm(orderId);
      toast.success(`Đã gửi bếp ${result.sentItemCount} món`);
      refresh();
    } catch (cause: unknown) {
      toast.error('Không gửi bếp được', errorMessage(cause));
    }
  };

  const closeSession = async (): Promise<void> => {
    const reason = await confirm({
      title: 'Đóng bàn',
      message: 'Bàn sẽ chuyển sang trạng thái cần dọn. Không đóng được nếu hoá đơn còn nợ.',
      confirmLabel: 'Đóng bàn',
    });
    if (reason === null) return;
    try {
      await api.orders.closeSession(sessionId, { reason });
      toast.success('Đã đóng bàn');
      onBack();
      onChanged();
    } catch (cause: unknown) {
      toast.error('Không đóng được bàn', errorMessage(cause));
    }
  };

  // --- printing -------------------------------------------------------------

  const printBill = (provisional: boolean, invoiceNo: string | null): void => {
    if (detail.data === null) return;
    const ok = printHtml(
      buildReceiptHtml(
        {
          restaurantName: me?.tenant.name ?? 'SonAuto',
          branchName: me?.branches.find((b) => b.id === branchId)?.name ?? '',
          tableLabel,
          cashierName: me?.user.fullName ?? '',
          invoiceNo,
          printedAt: Date.now(),
          provisional,
        },
        detail.data,
      ),
      provisional ? 'Phiếu tạm tính' : 'Hoá đơn',
    );
    if (!ok) toast.error('Không mở được khung in', 'Trình duyệt kiosk có thể đã chặn chức năng in.');
  };

  const issueInvoice = async (): Promise<void> => {
    const order = detail.data?.orders.find((o) => o.status !== 'CANCELLED');
    if (order === undefined) return;
    try {
      const result = await api.payments.invoice(order.id, { invoiceType: 'RECEIPT' });
      setInvoice(result);
      // Two different response shapes on the same endpoint — branch on the
      // discriminator, never on the HTTP status.
      printBill(false, result.invoiceNo);
      toast.success(isExistingInvoice(result) ? `Hoá đơn ${result.invoiceNo} đã phát hành trước đó` : `Đã phát hành ${result.invoiceNo}`);
    } catch (cause: unknown) {
      toast.error('Không phát hành được hoá đơn', errorMessage(cause));
    }
  };

  /**
   * Issue a fresh QR ordering token and print the slip with a REAL scannable
   * code. The plaintext token exists only in this one response (the server
   * stores a hash), so the slip is rendered and printed immediately — there is
   * no "reprint the same code" path, only "issue a new one".
   */
  const printQrSlip = async (): Promise<void> => {
    setPrintingQr(true);
    try {
      const issued = await api.orders.issueQrToken(sessionId);
      // The QR encodes the `url` (`/q?t=<token>`), never the bare token.
      let qrSvg: string | null = null;
      try {
        qrSvg = await renderQrSvg(issued.url);
      } catch {
        qrSvg = null; // the slip still prints the URL + short code
      }
      const ok = printHtml(buildQrSlipHtmlFromPayload(issued.slip, qrSvg), 'Phiếu QR gọi món');
      if (!ok) toast.error('Không mở được khung in', 'Trình duyệt kiosk có thể đã chặn chức năng in.');
      else toast.success(`Đã phát hành phiếu QR (mã ${issued.shortCode})`, 'Mã cũ của bàn vẫn dùng được đến khi thu hồi.');
    } catch (cause: unknown) {
      toast.error('Không phát hành được phiếu QR', errorMessage(cause));
    } finally {
      setPrintingQr(false);
    }
  };

  if (detail.initialLoading) return <LoadingState label="Đang tải hoá đơn…" />;
  if (detail.error !== null && detail.data === null) {
    return <ErrorState message={detail.error.message} onRetry={detail.reload} />;
  }
  if (detail.data === null) return <LoadingState />;

  const data = detail.data;
  const liveOrders = data.orders.filter((o) => o.status !== 'CANCELLED' && o.status !== 'VOIDED');

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[1fr_22rem]">
      <section className="min-h-0 overflow-y-auto p-3">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={onBack}>
            ← Sơ đồ bàn
          </Button>
          <h1 className="text-lg font-bold text-fg">{tableLabel}</h1>
          <StatusBadge status={data.session.status} />
          <span className="text-sm text-fg-subtle">
            Mở lúc {hhmm(data.session.opened_at)} · {data.session.guest_count} khách
          </span>
        </div>

        {data.serviceRequests.length > 0 ? (
          <Card className="mb-3" title="Khách đang yêu cầu">
            <ul className="space-y-1">
              {data.serviceRequests.map((request) => (
                <li key={request.id} className="flex items-center gap-2 text-sm">
                  <Badge tone={request.request_code === 'REQUEST_PAYMENT' ? 'danger' : 'warning'} size="sm">
                    {request.request_code}
                  </Badge>
                  <span className="text-flow text-fg-muted">{request.free_text_vi ?? ''}</span>
                  <span className="ml-auto text-xs text-fg-subtle">{hhmm(request.requested_at)}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {liveOrders.length === 0 ? (
          <EmptyState title="Bàn chưa gọi món" description="Bấm “Gọi món” để bắt đầu." />
        ) : null}

        {liveOrders.map((order) => (
          <Card
            key={order.id}
            className="mb-3"
            title={`Đơn ${order.order_no}`}
            description={`${order.status} · ${order.payment_status}`}
            actions={
              <div className="flex gap-2">
                <PermissionGate permission="order.confirm">
                  <Button size="sm" onClick={() => void sendToKitchen(order.id)}>
                    Gửi bếp
                  </Button>
                </PermissionGate>
                <PermissionGate permission="order.edit">
                  <Button size="sm" variant="secondary" onClick={() => onAddItems(order.id)}>
                    Gọi thêm
                  </Button>
                </PermissionGate>
              </div>
            }
          >
            <ul className="divide-y divide-line">
              {order.items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="w-8 text-center text-sm font-bold sa-tabular">{item.quantity}</span>
                  <span className="text-flow min-w-0 flex-1 text-sm text-fg">
                    {item.product_name_snapshot}
                    {item.is_gift === 1 ? <Badge tone="accent" size="sm" className="ml-2">Tặng</Badge> : null}
                  </span>
                  <StatusBadge status={item.status} size="sm" />
                  <MoneyText
                    amountMinor={item.is_gift === 1 ? 0 : item.line_total_minor}
                    currency={currency}
                    exponent={exponent}
                    className="w-28 text-right text-sm"
                  />
                  <PermissionGate permission="order.gift_item">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={item.is_gift === 1}
                      onClick={() => void giftItem(order.id, item.id, item.product_name_snapshot)}
                    >
                      Tặng
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission="order.item.cancel">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void cancelItem(order.id, item.id, item.product_name_snapshot)}
                    >
                      Huỷ
                    </Button>
                  </PermissionGate>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </section>

      {/* --- totals & actions --------------------------------------------- */}
      <aside className="flex min-h-0 flex-col border-l border-line bg-surface">
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          <Card title="Tổng cộng" padded>
            <dl className="space-y-1 text-sm">
              <Row label="Tổng hoá đơn" amount={data.balance.totalMinor} currency={currency} exponent={exponent} />
              <Row label="Đã thanh toán" amount={data.balance.paidMinor} currency={currency} exponent={exponent} />
              {data.balance.refundedMinor > 0 ? (
                <Row label="Đã hoàn" amount={data.balance.refundedMinor} currency={currency} exponent={exponent} />
              ) : null}
              <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
                <dt className="font-semibold text-fg">Còn lại</dt>
                <dd>
                  <MoneyText
                    amountMinor={data.balance.outstandingMinor}
                    currency={currency}
                    exponent={exponent}
                    className="text-xl font-bold"
                  />
                </dd>
              </div>
            </dl>
          </Card>

          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => onAddItems(null)}>Gọi món</Button>
            <PermissionGate permission="payment.receive">
              <Button variant="success" onClick={() => setPaying(true)}>
                Thanh toán
              </Button>
            </PermissionGate>
            <Button variant="secondary" onClick={() => printBill(true, null)}>
              In tạm tính
            </Button>
            <PermissionGate permission="invoice.print">
              <Button variant="secondary" onClick={() => void issueInvoice()}>
                Xuất hoá đơn
              </Button>
            </PermissionGate>
            <PermissionGate permission="order.discount">
              <Button variant="secondary" onClick={() => void applyDiscount()}>
                Giảm giá
              </Button>
            </PermissionGate>
            <PermissionGate permission="table.open">
              <Button variant="secondary" loading={printingQr} onClick={() => void printQrSlip()}>
                In phiếu QR
              </Button>
            </PermissionGate>
            <PermissionGate anyOf={['table.transfer', 'table.merge', 'table.split', 'order.transfer_item']}>
              <Button variant="secondary" onClick={() => setTransfer('MOVE_TABLE')}>
                Chuyển / ghép / tách
              </Button>
            </PermissionGate>
            <PermissionGate permission="table.close">
              <Button variant="danger" onClick={() => void closeSession()}>
                Đóng bàn
              </Button>
            </PermissionGate>
          </div>

          {invoice !== null ? (
            <Card title="Hoá đơn đã phát hành" padded>
              <p className="text-sm text-fg">
                {invoice.invoiceNo} · {invoice.status}
              </p>
              <PermissionGate permission="invoice.reprint">
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => {
                    void (async () => {
                      const reason = await confirm({
                        title: 'In lại hoá đơn',
                        message: 'Mỗi lần in lại đều được ghi nhật ký chống gian lận.',
                        requireReason: true,
                        reasonLabel: 'Lý do in lại',
                        reasonSuggestions: ['Máy in kẹt giấy', 'Khách yêu cầu bản sao', 'In mờ'],
                        tone: 'danger',
                      });
                      if (reason === null) return;
                      try {
                        const result = await api.payments.reprint(invoice.id, { printType: 'REPRINT' }, reason);
                        printBill(false, invoice.invoiceNo);
                        toast.success(`Đã in lại (lần ${result.printCount})`);
                      } catch (cause: unknown) {
                        toast.error('Không in lại được', errorMessage(cause));
                      }
                    })();
                  }}
                >
                  In lại hoá đơn
                </Button>
              </PermissionGate>
            </Card>
          ) : null}
        </div>
      </aside>

      {paying ? (
        <PaymentScreen
          branchId={branchId}
          detail={data}
          cashSessionId={cashSessionId}
          onClose={() => {
            setPaying(false);
            refresh();
          }}
          onPaid={refresh}
        />
      ) : null}

      {transfer !== null ? (
        <TransferDialog
          branchId={branchId}
          sessionId={sessionId}
          detail={data}
          tables={tables}
          onClose={() => setTransfer(null)}
          onDone={() => {
            setTransfer(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function Row({
  label,
  amount,
  currency,
  exponent,
}: {
  readonly label: string;
  readonly amount: number;
  readonly currency: string;
  readonly exponent: number;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-fg-muted">{label}</dt>
      <dd>
        <MoneyText amountMinor={amount} currency={currency} exponent={exponent} />
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Move / merge / split.
 *
 * All six shapes live behind ONE endpoint (`POST /v1/orders/transfer`) with a
 * different required-field set each. The dialog only enables the fields the
 * chosen `transferType` actually needs, so the API's Vietnamese "Cần
 * fromSessionId và toTableId." errors should never be reachable from here.
 */
function TransferDialog({
  branchId,
  sessionId,
  detail,
  tables,
  onClose,
  onDone,
}: {
  readonly branchId: string;
  readonly sessionId: string;
  readonly detail: SessionDetailResponse;
  readonly tables: readonly FloorMapTable[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [type, setType] = useState<TransferType>('MOVE_TABLE');
  const [toTableId, setToTableId] = useState('');
  const [toSessionId, setToSessionId] = useState('');
  const [itemIds, setItemIds] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);

  const firstOrderId = detail.orders[0]?.id ?? null;
  const needsTable = type === 'MOVE_TABLE' || type === 'SPLIT_TABLE';
  const needsSession = type === 'MERGE_TABLE';
  const needsItems = type === 'MOVE_ITEM' || type === 'SPLIT_BILL';

  const submit = async (): Promise<void> => {
    const reason = await confirm({
      title: 'Xác nhận thao tác chuyển bàn',
      message: 'Thao tác này nhạy cảm và được ghi nhật ký.',
      requireReason: true,
      reasonLabel: 'Lý do',
      reasonSuggestions: ['Khách đổi bàn', 'Ghép bàn cho nhóm đông', 'Tách hoá đơn theo yêu cầu'],
      tone: 'danger',
    });
    if (reason === null) return;
    setBusy(true);
    try {
      await api.orders.transfer(
        {
          transferType: type,
          branchId,
          fromSessionId: sessionId,
          toSessionId: toSessionId === '' ? null : toSessionId,
          toTableId: toTableId === '' ? null : toTableId,
          fromOrderId: firstOrderId,
          orderIds: type === 'SPLIT_TABLE' ? detail.orders.map((o) => o.id) : [],
          itemIds: needsItems ? [...itemIds] : [],
        },
        reason,
      );
      toast.success('Đã thực hiện');
      onDone();
    } catch (cause: unknown) {
      toast.error('Không thực hiện được', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Chuyển bàn / ghép bàn / tách hoá đơn"
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Huỷ
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            Thực hiện
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Select
          value={type}
          onChange={(e) => setType(e.target.value as TransferType)}
          options={[
            { value: 'MOVE_TABLE', label: 'Chuyển cả bàn sang bàn khác' },
            { value: 'MERGE_TABLE', label: 'Ghép bàn này vào phiên bàn khác' },
            { value: 'SPLIT_TABLE', label: 'Tách một phần sang bàn mới' },
            { value: 'MOVE_ITEM', label: 'Chuyển món sang đơn khác' },
            { value: 'SPLIT_BILL', label: 'Tách hoá đơn (tạo đơn mới)' },
            { value: 'MERGE_BILL', label: 'Gộp hoá đơn' },
          ]}
        />

        {needsTable ? (
          <Select
            value={toTableId}
            onChange={(e) => setToTableId(e.target.value)}
            placeholder="— Chọn bàn đích —"
            options={tables
              .filter((t) => t.isActive && t.id !== detail.session.table_id)
              .map((t) => ({ value: t.id, label: `${t.displayName} (${t.status})` }))}
          />
        ) : null}

        {needsSession ? (
          <Select
            value={toSessionId}
            onChange={(e) => setToSessionId(e.target.value)}
            placeholder="— Chọn phiên bàn đích —"
            options={tables
              .filter((t) => t.session !== null && t.session.id !== sessionId)
              .map((t) => ({ value: t.session?.id ?? '', label: `${t.displayName} · ${t.session?.code ?? ''}` }))}
          />
        ) : null}

        {needsItems ? (
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium text-fg">Chọn món cần chuyển</legend>
            {detail.orders.flatMap((order) =>
              order.items.map((item) => (
                <label key={item.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={itemIds.includes(item.id)}
                    onChange={(e) =>
                      setItemIds(
                        e.target.checked ? [...itemIds, item.id] : itemIds.filter((id) => id !== item.id),
                      )
                    }
                  />
                  <span className="text-flow">
                    {item.quantity} × {item.product_name_snapshot}
                  </span>
                </label>
              )),
            )}
          </fieldset>
        ) : null}

        <p className="text-flow text-xs text-fg-subtle">
          Lưu ý: nếu đơn nguồn đã có thanh toán, máy chủ sẽ từ chối chuyển món / tách hoá đơn.
        </p>
      </div>
    </Modal>
  );
}
